import React, { PropsWithChildren } from 'react';
import { neutralWhite } from '../themes/colors';
import styled, { css } from 'styled-components';
import { Icon } from '../icon';
import { Loader } from '../loader';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { FormInputSize } from '../utils/formInputSize';
import { DropdownSearchIcon } from './Dropdown.styled';

const StyledChevron = styled(Icon)`
	flex: 0 1 auto;
	color: ${props => props.theme.form.dropdown.chevron.color};
	margin-left: 4px;
`;

export const StyledDropdownBox = styled.div<{
	$open: boolean;
	$restricted?: boolean;
	$disabled?: boolean;
	$hasError?: boolean;
	$size: FormInputSize;
}>`
	font-family: ${props => props.theme.font};
	flex: 1 0 auto;
	border: 1px solid ${props => props.theme.form.input.border};
	display: flex;
	background: ${neutralWhite};
	cursor: pointer;
	gap: 8px;
	min-height: ${props => props.theme.form.input.size[props.$size].height}px;
	max-height: ${props => props.theme.form.input.size[props.$size].height}px;
	height: ${props => props.theme.form.input.size[props.$size].height}px;
	padding: ${props => props.theme.form.input.size[props.$size].padding};
	font-size: ${props => props.theme.form.input.size[props.$size].fontSize}px;
	align-items: center;
	color: ${props => props.theme.form.input.color};
	border-radius: 2px;
	overflow: hidden;
	box-sizing: border-box;

	${({ $hasError, theme }) => $hasError && css`
		border: 1px solid ${theme.form.input.error.border};
		box-shadow: ${theme.form.input.error.shadow};
	`}
	
	${DropdownSearchIcon} {
		font-size: ${props => props.theme.form.input.size[props.$size].iconSize}px;
	}

	&:focus {
		border-color: ${props => props.theme.form.input.focus.border};
		box-shadow: ${props => props.theme.form.input.focus.shadow};
	}
	
	${({ $open }) => $open && css`
		border-color: ${props => props.theme.form.input.focus.border};
		box-shadow: ${props => props.theme.form.input.focus.shadow};

		${StyledChevron} {
			color: ${props => props.theme.form.input.accentColor};
		}
	`}

	${({ $restricted, theme }) => $restricted && css`
		border: 1px solid ${theme.form.dropdown.restricted.border};
	`}

	${({ $disabled, theme }) => $disabled && css`
		background: ${theme.form.disabled.background};
		opacity: ${theme.form.disabled.opacity};
		cursor: auto;
	`}
`;

const StyledLoader = styled(Loader)`
	flex: 0 1 auto;
	margin: 2px;
`;

type BoxProps = {
	disabled?: boolean,
	hasError?: boolean,
	isOpen: boolean,
	loading?: boolean,
	onClose: () => void,
	onOpen: () => void,
	onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void,
	restricted?: boolean,
	size: FormInputSize,
	tabbable?: boolean,
};

export const Box = React.forwardRef<HTMLDivElement, PropsWithClassName<PropsWithChildren<BoxProps>>>(({
	className,
	disabled,
	hasError,
	isOpen,
	loading,
	onClose,
	onOpen,
	onKeyDown,
	restricted,
	size,
	tabbable,
	children
}, buttonRef) => {

	return (
		<StyledDropdownBox
			ref={buttonRef}
			tabIndex={tabbable ? (disabled ? -1 : 0) : undefined}
			onClick={loading || isOpen || disabled ? undefined : onOpen}
			onKeyDown={tabbable ? onKeyDown : undefined}
			className={className}
			$size={size}
			$open={isOpen}
			$disabled={disabled}
			$hasError={hasError}
			$restricted={restricted}>
			{children}
			{loading
				? <StyledLoader />
				: <StyledChevron
					icon={'dropdown-chevron'}
					onClick={isOpen ? onClose : undefined} />}
		</StyledDropdownBox>
	);
});

Box.displayName = 'DropdownBox';
